import { useState, useEffect, createContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Button } from "@mui/material";
import { treeActions } from "../../redux/slices/treeSlice";
import { api } from "../../redux/api";
import Inbound from "./tabs/Inbound";
import Outbound from "./tabs/Outbound";
import Workflow from "./tabs/Workflow";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import TabPanel from "../../components/TabPanel";
import SaveButton from "../../components/Save/SaveButton";
import HelpPanel from "../../components/Help/HelpPanel";
import RepositoryButton from "../../components/Repository/RepositoryButton";
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import CategoryIcon from '@mui/icons-material/Category';
import axios from "axios";
import { setLoadTemplateOnLanding } from "../../redux/slices/queryParamsSlice";
import MappingButton from "../../components/Mapping/MappingButton";
import ApiServiceWizardReturnButton from "../../components/Admin/ApiServiceManagement/ApiServiceWizardReturnButton";
import ApiServiceWizardMappingLanding from "../../components/Admin/ApiServiceManagement/ApiServiceWizardMappingLanding";
import VisualWorkflowButton from "../../components/VisualWorkflow/VisualWorkflowButton";
import VisualWorkflow from "../../components/VisualWorkflow/VisualWorkflow";
import { cloneDeep } from "lodash";

export const ParentScreenCtx = createContext();

function WorkbenchBuild({urlParams}) {
  const { t } = useTranslation();
  // const [loaded, setLoaded] = useState(false);
  const state = useSelector((state) => state);
  const savedQueryParams = useSelector((state) => state.queryParams);
  const [currTab, setCurrTab] = useState(0);
  const dispatch = useDispatch();
  const [uniqueKey, setUniqueKey] = useState(0);
  const currTabBodyData = state.treeData[state.treeData.currentScreen]?.leftTree?.tree?.body?.translatedData || false;
  const [templateLoadOnLanding, setTemplateLoadOnLanding] = useState(false);
  const treeData = useSelector(state => state.treeData);
 
  const handleTabChange = (newValue) => {
    setUniqueKey(prev => prev + 1 )
    const screenTabs = ['inbound', 'outbound', 'workflow'];
    const prevTab = screenTabs[currTab];
    const templateChanged = treeData[prevTab]?.templateChanged;
    if (templateChanged) {
      dispatch(
        treeActions.setLeftTreeContextActionValue({
          screen: prevTab,
          data: {
            name: treeData[prevTab].leftTree.tree.body.selectedNode.title,
            workflowGroup: treeData[prevTab].leftTree.tree.body.selectedNode.workflowGroup,
          },
        })
      );
    } 
    dispatch(treeActions.setCurrentScreen(screenTabs[newValue]));
    dispatch(api.endpoints.sendLastSelectedTab.initiate({tab: screenTabs[newValue].toUpperCase(), account: urlParams.account}));
    dispatch(treeActions.setIsMappingMode(false))
    setCurrTab(newValue);

    if (newValue === 2) {
      const workflowSelectedNode = cloneDeep(treeData.workflow.leftTree.tree.body.selectedNode);
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: "workflow",
          type: "body",
          data: {}
        })
      );
      setTimeout(() => {
        dispatch(
          treeActions.setRightTreeSelectedNode({
            screen: "workflow",
            type: "body",
            data: workflowSelectedNode
          })
        );
      });
    }
    
  };

  useEffect(()=>{
    ['inbound','outbound','workflow'].forEach((e,idx)=>{
      if(e === state.treeData.currentScreen){
        setCurrTab(idx)
      }
    })
  },[state?.treeData?.currentScreen]);

  useEffect(() => {
    const normalizedTab = (urlParams.tab || "").toLowerCase();
    //set current tab based on "tab" url param
    if (normalizedTab === "outbound") setCurrTab(1);
    if (normalizedTab === "workflow") setCurrTab(2);

    //save the current tab to backend to keep it insync incase backend needs to
    // know the current tab when saving a last selected template.
    if (["inbound", "outbound", "workflow"].includes(normalizedTab)) {
      dispatch(api.endpoints.sendLastSelectedTab.initiate({tab: normalizedTab.toUpperCase(), account: urlParams.account}));
    }
    
    //fetch unsaved templates
    dispatch(api.endpoints.getUnsavedTemplates.initiate(
      { account: urlParams.account }, { forceRefetch: true }
    ));

    dispatch(treeActions.setCurrentScreen(["inbound", "outbound", "workflow"].includes(normalizedTab) ? normalizedTab : "inbound"))
    dispatch(api.endpoints.getHelpTreeList.initiate({account:savedQueryParams.account}));
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      !templateLoadOnLanding &&
      currTabBodyData.length > 0 && 
      urlParams.workflowGroupName && 
      urlParams.template && 
      urlParams.baseUrl &&
      urlParams.user &&
      urlParams.account
    ) {
      setTemplateLoadOnLanding(true);
      dispatch(setLoadTemplateOnLanding(true));
    }
  }, [currTabBodyData]);

  const openHelpPanel = () => {
    dispatch(treeActions.setShowHelpPanel(true));
  }

  return (
    <div style={{overflow:'hidden'}} className={"wbb-main-container"}>
      <ApiServiceWizardMappingLanding urlParams={urlParams} />
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <div className="flex space-between padding-4">
          {/* Left side buttons */}
          <div className="flex">
            <Button
              size='small'
              variant="outlined"
              className={
                "margin-right-4 " +
                (currTab === 0 ? "selected-tab-button" : "")
              }
              sx={{paddingLeft: '6px !important'}}
              onClick={() => {
                setTimeout(() => {
                  handleTabChange(0);
                });
              }}
            >
              <InputIcon fontSize="small" className="margin-right-4"/>
              {t("INBOUND")}
            </Button>
            <Button
              size='small'
              variant="outlined"
              className={
                "margin-right-4 " +
                (currTab === 1 ? "selected-tab-button" : "")
              }
              sx={{paddingLeft: '6px !important'}}
              onClick={() => {
                setTimeout(() => {
                  handleTabChange(1);
                });
              }}
            >
              <OutputIcon fontSize="small" className="margin-right-4"/>
              {t("OUTBOUND")}
            </Button>
            <Button
              size='small'
              variant="outlined"
              className={currTab === 2 ? "selected-tab-button" : ""}
              sx={{paddingLeft: '6px !important'}}
              onClick={() => {
                setTimeout(() => {
                  handleTabChange(2);
                });
              }}
            >
              <CategoryIcon fontSize="small" className="margin-right-4"/>
              {t("STAT_WORKFLOW")}
            </Button>
          </div>

          {/* Right side buttons */}
          <div className="flex">
            <VisualWorkflowButton handleOnClick={() => {
              setTimeout(() => {
               currTab === 2 ? setCurrTab(3) : handleTabChange(2);
              });
            }} />
            <ApiServiceWizardReturnButton />
            <MappingButton/>
            <SaveButton />
            <RepositoryButton />
            <Button
              size='small'
              variant="outlined"
              onClick={() => {
                openHelpPanel();
                // alert('Help functionality is not yet functional');
              }}
            >
              <HelpOutlineIcon fontSize="medium" className="margin-right-4" />
              {t("HELP")}
            </Button>
          </div>
        </div>
      </Box>
      <Box className={"wbb-screen-content"}>
        <TabPanel value={currTab} index={0} className={"height-100"}>
          <div className={"height-100"}>
            <ParentScreenCtx.Provider value="inbound">
              <Inbound val={uniqueKey}  />
              <HelpPanel />
            </ParentScreenCtx.Provider>
          </div>
        </TabPanel>
        <TabPanel value={currTab} index={1} className={"height-100"}>
          <div className={"height-100"}>
            <ParentScreenCtx.Provider value="outbound">
              <Outbound val={uniqueKey} />
            </ParentScreenCtx.Provider>
          </div>
        </TabPanel>
        <TabPanel value={currTab} index={2} className={"height-100"}>
          <ParentScreenCtx.Provider value="workflow">
            <Workflow val={uniqueKey} />
          </ParentScreenCtx.Provider>
          {/* <div>{t('CONTENT_UNDER_CONSTRUCTION')}</div> */}
        </TabPanel>
        {
          currTab === 3 && (
            <TabPanel value={currTab} index={3} className={"height-100"}>
            <div className={"height-100"}>
              <ParentScreenCtx.Provider value="workflow">
                <VisualWorkflow />
              </ParentScreenCtx.Provider>
            </div>
          </TabPanel>
          )
        }
      </Box>
    </div>
  );
}

export default WorkbenchBuild;